import * as React from 'react';
import {mapValues} from 'lodash';
import {type Point} from '@packages/utilities/PointUtils';
import {emotionStyled} from '@packages/utilities/styled';
import {emptyObject, isNil, identity} from '@packages/utilities/fp/utils';
import {Input} from './Input';
// eslint-disable-next-line webflow/no-dangerous-imports
import DragGhost from '@packages/systems/ui/shared-ui/DragGhost';
import PseudoProvider from './PseudoProvider';
import draggable from './logic/draggable';
import WebflowRightInputTicks from './WebflowRightInputTicks';
import {
  isValidNumber,
  clamp,
  parseNumber,
  stepWithFloatingPointPrecision,
  toPrecision,
} from '@packages/systems/core/utils/NumberUtils';
import {parseMultiValue} from '@packages/systems/style-legacy/utils';
import {flushSync} from 'react-dom';

const document = global && global.document;

export type OnChangeMeta = {
  changeType: 'changeStart' | 'change' | 'changeEnd' | 'changeCancel';
  action?: Record<any, any>;
  type?: string;
};

type Config = {
  min: number;
  max: number;
  step: number;
  isValid?: (arg1: string) => boolean;
  optional?: boolean;
  dragSensitivity?: number;
  castKeywordAsNumber?: (arg1: string) => string | null;
  validateOnKeyPress?: boolean;
  allowKeywordDrag?: boolean;
};

type GridItemProps = {
  gridColumn?: number | string;
  gridColumnStart?: number | string;
  gridColumnEnd?: number | string;
  gridRow?: number | string;
  gridRowStart?: number | string;
  gridRowEnd?: number | string;
  alignSelf?: string;
  justifySelf?: string;
  placeSelf?: string;
};

type Props = Config &
  GridItemProps & {
    name?: string;
    style?: Partial<React.CSSProperties>;
    onChange: (value: number, meta: OnChangeMeta) => void;
    onReset?: () => void;
    disabled?: boolean;
    placeholder?: string;
    value?: number;
    ['data-automation-id']?: string;
    precision?: number;
    /** Setting true allows input ticks inside the input by default, otherwise, input ticks are shown on Option + hover */
    alwaysShowRightInputTicks?: boolean;
    onInputFocused?: () => void;
    onInputBlurred?: () => void;
  };

export type State = {
  invalid: boolean;
  focus: boolean;
  alt: boolean;
  shift: boolean;
  inputValue: string;
  changing: boolean;
  value?: number;
  draggedValue: number | null;
  precision: number | null;
};

const changeStartType = {changeType: 'changeStart'} as const;
const changeType = {changeType: 'change'} as const;
const changeEndType = {changeType: 'changeEnd'} as const;
const SHIFT_KEY_STEP_MODIFIER = 10;

export const WHEEL_TIMEOUT = 200;

export const actionTypes = {
  INPUT_FOCUSED: 'INPUT_FOCUSED' as const,
  INPUT_BLURRED: 'INPUT_BLURRED' as const,
  INPUT_VALUE_CHANGED: 'INPUT_VALUE_CHANGED' as const,
  INPUT_DRAG_STARTED: 'INPUT_DRAG_STARTED' as const,
  INPUT_DRAGGED: 'INPUT_DRAGGED' as const,
  INPUT_DRAG_ENDED: 'INPUT_DRAG_ENDED' as const,
  INPUT_WHEEL_STARTED: 'INPUT_WHEEL_STARTED' as const,
  INPUT_WHEELED: 'INPUT_WHEELED' as const,
  INPUT_WHEEL_ENDED: 'INPUT_WHEEL_ENDED' as const,
  INPUT_KEY_RELEASED: 'INPUT_KEY_RELEASED' as const,
  INPUT_ENTER_KEY_PRESSED: 'INPUT_ENTER_KEY_PRESSED' as const,
  INPUT_ARROW_UP_KEY_PRESSED: 'INPUT_ARROW_UP_KEY_PRESSED' as const,
  INPUT_ARROW_DOWN_KEY_PRESSED: 'INPUT_ARROW_DOWN_KEY_PRESSED' as const,
  INPUT_RIGHT_UP_TICK_CLICKED: 'INPUT_RIGHT_UP_TICK_CLICKED' as const,
  INPUT_RIGHT_DOWN_TICK_CLICKED: 'INPUT_RIGHT_DOWN_TICK_CLICKED' as const,
  ALT_KEY_TOGGLED: 'ALT_KEY_TOGGLED' as const,
  SHIFT_KEY_TOGGLED: 'SHIFT_KEY_TOGGLED' as const,
} as const;

type ArrowKeyPressedAction<TType> = {
  type: TType;
};

export type Action =
  | {
      type: typeof actionTypes.INPUT_FOCUSED;
    }
  | {
      type: typeof actionTypes.INPUT_BLURRED;
    }
  | {
      type: typeof actionTypes.INPUT_VALUE_CHANGED;
      payload: {
        value: string;
      };
    }
  | {
      type: typeof actionTypes.INPUT_DRAG_STARTED;
      payload?: {
        x: number;
        y: number;
        initialValue?: number;
      };
    }
  | {
      type: typeof actionTypes.INPUT_WHEEL_STARTED;
      payload?: {
        x: number;
        y: number;
        initialValue?: number;
      };
    }
  | {
      type: typeof actionTypes.INPUT_DRAGGED;
      payload: {
        x: number;
        y: number;
      };
    }
  | {
      type: typeof actionTypes.INPUT_WHEELED;
      payload: {
        x: number;
        y: number;
      };
    }
  | {
      type: typeof actionTypes.INPUT_DRAG_ENDED;
    }
  | {
      type: typeof actionTypes.INPUT_WHEEL_ENDED;
    }
  | {
      type: typeof actionTypes.INPUT_KEY_RELEASED;
    }
  | {
      type: typeof actionTypes.INPUT_ENTER_KEY_PRESSED;
    }
  | {
      type: typeof actionTypes.ALT_KEY_TOGGLED;
      payload: boolean;
    }
  | {
      type: typeof actionTypes.SHIFT_KEY_TOGGLED;
      payload: boolean;
    }
  | ArrowKeyPressedAction<typeof actionTypes.INPUT_RIGHT_DOWN_TICK_CLICKED>
  | ArrowKeyPressedAction<typeof actionTypes.INPUT_RIGHT_UP_TICK_CLICKED>
  | ArrowKeyPressedAction<typeof actionTypes.INPUT_ARROW_UP_KEY_PRESSED>
  | ArrowKeyPressedAction<typeof actionTypes.INPUT_ARROW_DOWN_KEY_PRESSED>;

export const actionCreators = {
  onDragStarted: (payload?: {
    x: number;
    y: number;
    initialValue?: null | number;
  }) => ({
    type: actionTypes.INPUT_DRAG_STARTED,
    payload,
  }),
  onDragEnded: () => ({type: actionTypes.INPUT_DRAG_ENDED}),
  onDragged: ({x, y}: {x: number; y: number}) => ({
    type: actionTypes.INPUT_DRAGGED,
    payload: {x, y},
  }),
  onWheelStarted: (payload?: {x: number; y: number}) => ({
    type: actionTypes.INPUT_WHEEL_STARTED,
    payload,
  }),
  onWheelEnded: () => ({type: actionTypes.INPUT_WHEEL_ENDED}),
  onWheeled: ({x, y}: {x: number; y: number}) => ({
    type: actionTypes.INPUT_WHEELED,
    payload: {x, y},
  }),
  onFocus: () => ({type: actionTypes.INPUT_FOCUSED}),
  onBlur: () => ({type: actionTypes.INPUT_BLURRED}),
  onChange: (e: React.ChangeEvent<EventTarget>) => ({
    type: actionTypes.INPUT_VALUE_CHANGED,
    payload: {
      // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'EventTarget'.
      value: e.target.value,
    },
  }),
  onRightUpTickClick: (e: React.MouseEvent<EventTarget>) => {
    e.stopPropagation();
    e.preventDefault();
    return {
      type: actionTypes.INPUT_RIGHT_UP_TICK_CLICKED,
    };
  },
  onRightDownTickClick: (e: React.MouseEvent<EventTarget>) => {
    e.stopPropagation();
    e.preventDefault();
    return {
      type: actionTypes.INPUT_RIGHT_DOWN_TICK_CLICKED,
    };
  },
  onKeyDown: (e: React.KeyboardEvent<EventTarget>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
    }

    if (e.key === 'Enter') {
      return {
        type: actionTypes.INPUT_ENTER_KEY_PRESSED,
      };
    }

    if (e.key === 'ArrowUp') {
      return {
        type: actionTypes.INPUT_ARROW_UP_KEY_PRESSED,
      };
    }

    if (e.key === 'ArrowDown') {
      return {
        type: actionTypes.INPUT_ARROW_DOWN_KEY_PRESSED,
      };
    }

    return emptyObject;
  },
  onKeyUp: (e: React.KeyboardEvent<EventTarget>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      return {type: actionTypes.INPUT_KEY_RELEASED};
    }

    return emptyObject;
  },

  onAltKeyUp: (event: KeyboardEvent) => {
    if (event.key === 'Option' || event.key === 'Alt') {
      return {type: actionTypes.ALT_KEY_TOGGLED, payload: false};
    }
    return emptyObject;
  },
  onAltKeyDown: (event: KeyboardEvent) => {
    if (event.key === 'Option' || event.key === 'Alt') {
      return {type: actionTypes.ALT_KEY_TOGGLED, payload: true};
    }
    return emptyObject;
  },
  onShiftKeyUp: (event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      return {type: actionTypes.SHIFT_KEY_TOGGLED, payload: false};
    }
    return emptyObject;
  },
  onShiftKeyDown: (event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      return {type: actionTypes.SHIFT_KEY_TOGGLED, payload: true};
    }
    return emptyObject;
  },
} as const;

const defaultDragSensitivity = 2;
const defaultCastKeywordAsNumber = identity;

function wheelReducer(
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type.
  state,
  // @ts-expect-error - TS7006 - Parameter 'action' implicitly has an 'any' type.
  action,
  config: Config,
  initialValue: undefined | number | string
) {
  const {
    min,
    max,
    step,
    dragSensitivity = defaultDragSensitivity,
    castKeywordAsNumber = defaultCastKeywordAsNumber,
    allowKeywordDrag = false,
  } = config;

  switch (action.type) {
    case actionTypes.INPUT_WHEELED: {
      // we only care about horizontal dragging
      const {x} = action.payload;
      const mouseDelta = x || 0;
      const dragOffsetValue = mouseDelta / dragSensitivity;

      if (!dragOffsetValue) {
        return state;
      }

      const number =
        state.value != null
          ? parseNumber(state.value)
          : initialValue && castKeywordAsNumber(String(initialValue));

      if (typeof number === 'number') {
        const currentDraggedValue = state.draggedValue
          ? state.draggedValue
          : number;
        const currentDragValue = clamp(
          min,
          max,
          currentDraggedValue + step * dragOffsetValue
        );

        const multiplier = step * (state.shift ? SHIFT_KEY_STEP_MODIFIER : 1);

        let newValue =
          dragOffsetValue < 0
            ? floorToDivisible(currentDragValue, multiplier)
            : ceilToDivisible(currentDragValue, multiplier);
        newValue = clamp(min, max, newValue);

        return {
          ...state,
          changing: true,
          inputValue: String(newValue),
          draggedValue: currentDragValue,
          value: newValue,
        };
      }
      return state;
    }

    case actionTypes.INPUT_WHEEL_STARTED: {
      if (!state.alt) {
        return state;
      }

      const parsedValue = Number(castKeywordAsNumber(state.inputValue) || 0);
      const isNonNumeric = Number.isNaN(parsedValue);

      if (isNonNumeric && !allowKeywordDrag) {
        return state;
      }

      // Fallback to 0 if parsing results in NaN (e.g., from keyword values like "auto")
      const value = parsedValue || 0;
      state = {
        ...state,
        changing: true,
        value,
      };

      // `initialValue` payload is used to pass in an optional drag start value,
      // including explicit `null` values (hence the `hasOwnProperty`` call)
      if (action.payload && action.payload.initialValue != null) {
        state = {
          ...state,
          draggedValue: action.payload.initialValue,
          value: action.payload.initialValue,
          inputValue: String(action.payload.initialValue),
        };
      }

      return state;
    }

    case actionTypes.INPUT_WHEEL_ENDED: {
      state = commitInputValue(state, config);
      if (!state.inputValue && initialValue != null) {
        state = resetValue(state, config, initialValue);
      }
      return state;
    }
  }
  return state;
}

export const reducer = (
  state: State,
  action: Action,
  config: Config,
  initialValue?: string | number
): State => {
  const {
    min,
    max,
    step,
    dragSensitivity = defaultDragSensitivity,
    castKeywordAsNumber = defaultCastKeywordAsNumber,
    optional = false,
    validateOnKeyPress = false,
    allowKeywordDrag = false,
  } = config;

  switch (action.type) {
    case actionTypes.INPUT_WHEELED:
    case actionTypes.INPUT_WHEEL_STARTED:
    case actionTypes.INPUT_WHEEL_ENDED: {
      return wheelReducer(state, action, config, initialValue);
    }

    case actionTypes.ALT_KEY_TOGGLED: {
      return {...state, alt: action.payload};
    }
    case actionTypes.SHIFT_KEY_TOGGLED: {
      return {...state, shift: action.payload};
    }

    case actionTypes.INPUT_FOCUSED: {
      return state.focus
        ? state
        : {
            ...state,
            focus: true,
          };
    }

    case actionTypes.INPUT_BLURRED: {
      state = commitInputValue(state, config);

      // If we blur with an invalid value, fallback to the initial value if it exists.
      if (!config.optional && !state.inputValue && initialValue != null) {
        state = resetValue(state, config, initialValue);
      }

      return {...state, focus: false, changing: false};
    }

    case actionTypes.INPUT_DRAGGED: {
      const {draggedValue} = state;

      if (draggedValue == null) {
        // If props value exists, we set that as the drag start value.
        // This value will be used to calculate the delta throught dragging
        if (initialValue != null) {
          return {...state, draggedValue: Number(initialValue)};
        }
        return state;
      }

      // we only care about horizontal dragging
      const {x} = action.payload;
      let newValue = clamp(min, max, draggedValue + x * step);

      const dragOffsetValue = x / dragSensitivity;
      const multiplier = step * (state.shift ? SHIFT_KEY_STEP_MODIFIER : 1);

      newValue = clamp(
        min,
        max,
        dragOffsetValue < 0
          ? floorToDivisible(newValue, multiplier)
          : ceilToDivisible(newValue, multiplier)
      );

      if (newValue === state.value) {
        return state;
      }

      return {
        ...state,
        changing: true,
        inputValue: String(newValue),
        value: newValue,
      };
    }

    case actionTypes.INPUT_VALUE_CHANGED: {
      const valid =
        action.payload.value === ''
          ? optional
          : isValidNumber(action.payload.value);

      return {
        ...state,
        invalid: validateOnKeyPress ? !valid : state.invalid,
        inputValue: action.payload.value,
      };
    }

    case actionTypes.INPUT_DRAG_STARTED: {
      if (!state.alt) {
        return state;
      }

      const parsedValue = Number(castKeywordAsNumber(state.inputValue) ?? min);
      const isNonNumeric = Number.isNaN(parsedValue);

      if (isNonNumeric && !allowKeywordDrag) {
        return state;
      }

      // Fallback to min if parsing results in NaN (e.g., from keyword values like "auto")
      const value = parsedValue || min;
      state = {
        ...state,
        changing: true,
        value,
        draggedValue: value,
      };

      // `initialValue` payload is used to pass in an optional drag start value,
      // including explicit `null` values (hence the `hasOwnProperty`` call)
      if (action.payload && action.payload.initialValue != null) {
        state = {
          ...state,
          draggedValue: action.payload.initialValue,
          value: action.payload.initialValue,
          inputValue: String(action.payload.initialValue),
        };
      }

      return state;
    }
    case actionTypes.INPUT_ENTER_KEY_PRESSED: {
      state = commitInputValue(state, config);
      if (!state.inputValue) {
        state = {
          ...state,
          changing: false,

          // set to null so that we can flag for resetting
          value: undefined,
        };
      }
      return state;
    }

    case actionTypes.INPUT_DRAG_ENDED: {
      if (!state.changing || state.draggedValue == null) return state;

      state = commitInputValue(state, config);
      state = {...state, draggedValue: null};
      if (!state.inputValue && initialValue != null) {
        state = resetValue(state, config, initialValue);
      }
      return state;
    }

    case actionTypes.INPUT_ARROW_UP_KEY_PRESSED: {
      return handleIncrementAction(state, action, config);
    }

    case actionTypes.INPUT_ARROW_DOWN_KEY_PRESSED: {
      return handleDecrementAction(state, action, config);
    }

    case actionTypes.INPUT_RIGHT_UP_TICK_CLICKED: {
      return handleIncrementAction(state, action, config);
    }

    case actionTypes.INPUT_RIGHT_DOWN_TICK_CLICKED: {
      return handleDecrementAction(state, action, config);
    }

    case actionTypes.INPUT_KEY_RELEASED: {
      return state.changing
        ? {
            ...state,
            changing: false,
          }
        : state;
    }

    default: {
      /*::
      // Ensure we're handling all possible action types:
      (action: empty);
      */
      return state;
    }
  }
};

const commitInputValue = (state: State, {optional, min, max}: Config) => {
  const {inputValue, precision} = state;
  const value =
    typeof precision === 'number'
      ? parseNumber(toPrecision(inputValue, precision))
      : parseNumber(inputValue);

  const valid = inputValue === '' ? optional : isValidNumber(value);

  if (valid) {
    const clampedValue = inputValue === '' ? undefined : clamp(min, max, value);
    return {
      ...state,
      changing: false,
      inputValue: isNil(clampedValue) ? '' : String(clampedValue),
      value: clampedValue,
    };
  } else {
    // Handles if users are trying to do math to calculate final value
    const maybeValidValue = parseMultiValue(inputValue).value || null;

    if (maybeValidValue && isValidNumber(maybeValidValue)) {
      const clampedValue = clamp(min, max, maybeValidValue);
      return {
        ...state,
        changing: false,
        value: clampedValue,
      };
    }

    return {...state, changing: false, invalid: true};
  }
};

const handleIncrementAction = (
  state: State,
  action: ArrowKeyPressedAction<any>,
  config: Config
) => {
  return handleStepAction(state, action, config, ceilToDivisible);
};

const handleDecrementAction = (
  state: State,
  action: ArrowKeyPressedAction<any>,
  config: Config
) => {
  return handleStepAction(state, action, config, floorToDivisible);
};

const handleStepAction = (
  state: State,
  action: ArrowKeyPressedAction<any>,
  {
    min,
    max,
    step,
    isValid = isValidNumber,
    castKeywordAsNumber = defaultCastKeywordAsNumber,
  }: Config,
  stepValue: (value: number, multiplier?: number) => number
) => {
  const inputValue = castKeywordAsNumber(state.inputValue) || '0';

  const valid = isValid(inputValue);

  if (valid) {
    const number = parseNumber(inputValue);
    let newValue = stepValue(
      number,
      step * (state.shift ? SHIFT_KEY_STEP_MODIFIER : 1)
    );
    newValue = clamp(min, max, newValue);

    const changing =
      action.type !== actionTypes.INPUT_RIGHT_UP_TICK_CLICKED &&
      action.type !== actionTypes.INPUT_RIGHT_DOWN_TICK_CLICKED;

    return {
      ...state,
      inputValue: String(newValue),
      changing,
      value: newValue,
    };
  }

  if (!valid) {
    return {...state, invalid: true};
  }

  return state;
};

const resetValue = (
  state: State,
  {min, max}: Config,
  initialValue?: string | number
) => {
  const clampedValue = clamp(min, max, Number(initialValue));
  if (isNaN(clampedValue)) {
    return {
      ...state,
      invalid: false,
      inputValue: String(initialValue),
    };
  }
  return {
    ...state,
    invalid: false,
    inputValue: String(clampedValue),
    value: clampedValue,
  };
};

// @ts-expect-error - TS7006 - Parameter 'prevState' implicitly has an 'any' type. | TS7006 - Parameter 'action' implicitly has an 'any' type.
const getChangeType = (nextState: State, prevState, action) => {
  switch (action.type) {
    case actionTypes.INPUT_WHEEL_STARTED:
    case actionTypes.INPUT_DRAG_STARTED:
      return changeStartType;

    case actionTypes.INPUT_WHEEL_ENDED:
    case actionTypes.INPUT_DRAG_ENDED:
    case actionTypes.INPUT_KEY_RELEASED: {
      return changeEndType;
    }

    case actionTypes.INPUT_WHEELED:
    case actionTypes.INPUT_DRAGGED:
    case actionTypes.INPUT_VALUE_CHANGED:
    case actionTypes.INPUT_BLURRED:
    case actionTypes.INPUT_RIGHT_UP_TICK_CLICKED:
    case actionTypes.INPUT_RIGHT_DOWN_TICK_CLICKED:
    case actionTypes.INPUT_ENTER_KEY_PRESSED:
      return changeType;

    case actionTypes.INPUT_ARROW_UP_KEY_PRESSED:
    case actionTypes.INPUT_ARROW_DOWN_KEY_PRESSED: {
      return nextState.changing && prevState.changing
        ? changeType
        : changeStartType;
    }
    default: {
      return null;
    }
  }
};

// Calculates multipler to convert value into number that can use modulus to
// find remainder using a given step value that is _not_. Note that returned value must be <= 10
// to ensure that (calculateRemainder(51, 1) === 0) doesn't happen.
const calcModuloDecimal = (value: number) => {
  let offset = 10;
  while ((value * offset) % 10) offset *= 10;
  return offset;
};

// Similar to modulo but accepts floats.
const calculateRemainder = (a: number, b: number) => {
  const decimal = Math.max(calcModuloDecimal(a), calcModuloDecimal(b));
  const remainder =
    (Math.round(a * decimal) % Math.round(b * decimal)) / decimal;
  return remainder;
};

const ceilToDivisible = (value: number, multiplier: number = 1) => {
  const remainder = calculateRemainder(value, multiplier);
  if (!remainder) {
    return stepWithFloatingPointPrecision(value, multiplier);
  }

  return stepWithFloatingPointPrecision(value, multiplier - remainder);
};

const floorToDivisible = (value: number, multiplier: number = 1) => {
  const remainder = calculateRemainder(value, multiplier);
  if (!remainder) {
    return stepWithFloatingPointPrecision(value, -multiplier);
  }

  return stepWithFloatingPointPrecision(value, -remainder);
};

const makeDispatchHandlers = (dispatch: (action: Action) => void) =>
  mapValues(
    actionCreators,
    (actionCreator) =>
      // @ts-expect-error - TS7019 - Rest parameter 'args' implicitly has an 'any[]' type.
      (...args) =>
        // @ts-expect-error - TS2345 - Argument of type 'Record<string, any>' is not assignable to parameter of type 'Action'. | TS2556 - A spread argument must either have a tuple type or be passed to a rest parameter.
        dispatch(actionCreator(...args))
  );

const validatePrecision = (precision?: number | null) => {
  return typeof precision === 'number' &&
    Number.isInteger(precision) &&
    precision >= 0
    ? precision
    : null;
};

const inputResizeCursor = {cursor: 'ew-resize'} as const;
const StyledTextInput = draggable(Input);

const disallowTextDragging = (e: any) => e.preventDefault();

export default class NumberInput extends React.PureComponent<Props, State> {
  declare wheelTimeout: number;
  // Initialized in constructor to work around SWC minification bug
  // See: https://github.com/swc-project/swc/issues/11368
  handlers: ReturnType<typeof makeDispatchHandlers>;

  state = {
    inputValue: isNil(this.props.value) ? '' : String(this.props.value),
    focus: false,
    alt: false,
    shift: false,
    invalid: false,
    changing: false,
    value: this.props.value,
    draggedValue: null,
    precision: validatePrecision(this.props.precision),
  };

  constructor(props: Props) {
    super(props);
    this.handlers = makeDispatchHandlers(this.dispatch);
  }

  static getDerivedStateFromProps(props: Props, state: State): null | State {
    if (props.value !== state.value) {
      return {
        ...state,
        inputValue: isNil(props.value) ? '' : String(props.value),
        invalid: false,
        value: props.value,
        precision: validatePrecision(props.precision),
      };
    }
    return null;
  }

  handleKeyDown = (e: KeyboardEvent) => {
    this.handlers.onAltKeyDown(e);
    this.handlers.onShiftKeyDown(e);
  };

  handleKeyUp = (e: KeyboardEvent) => {
    this.handlers.onAltKeyUp(e);
    this.handlers.onShiftKeyUp(e);
  };

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown, false);
    document.removeEventListener('keyup', this.handleKeyUp, false);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown, false);
    document.addEventListener('keyup', this.handleKeyUp, false);
  }

  dispatch = (action: Action) => {
    const newState = reducer(this.state, action, this.props, this.props.value);
    const meta = getChangeType(newState, this.state, action);
    const {
      onChange,
      onReset,
      onInputFocused,
      onInputBlurred,
      value,
      optional = false,
    } = this.props;

    const isValid = isNil(newState.value)
      ? optional
      : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        isValidNumber(newState.value);

    const hasUpdater = Boolean(isNil(newState.value) ? onReset : onChange);

    if (
      isValid &&
      hasUpdater &&
      meta &&
      (String(value) !== String(newState.value) ||
        meta.changeType === 'changeEnd' ||
        meta.changeType === 'changeStart')
    ) {
      const updater = isNil(newState.value)
        ? () => {
            // onReset existence test to make Flow happy
            onReset && onReset();
          }
        : () => {
            onChange(Math.max(this.props.min, Number(newState.value)), meta);
          };

      // Parents must be notified of the new value immediately or else
      // we will render with props containing the old value and waste
      // a render on the old value due to getDerivedStateFromProps
      // and its reliance on props.value !== state.value.
      //
      // This temporary "out-of-sync" render is not only unnecessary it causes
      // E2E tests to fail.
      flushSync(() => {
        this.setState(newState, updater);
      });
    } else {
      // See above comment on flushSync call.
      flushSync(() => {
        this.setState(newState);
      });
    }

    if (action.type === actionTypes.INPUT_FOCUSED && onInputFocused) {
      onInputFocused();
    } else if (action.type === actionTypes.INPUT_BLURRED && onInputBlurred) {
      onInputBlurred();
    }
  };

  handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    if (event.altKey) {
      event.stopPropagation();
      event.preventDefault();
      clearTimeout(this.wheelTimeout);
      // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
      this.wheelTimeout = setTimeout(this.handlers.onWheelEnded, WHEEL_TIMEOUT);
      const deltaY = event.deltaY;
      if (this.state.changing === false) {
        this.handlers.onWheelStarted();
      } else {
        this.handlers.onWheeled({x: deltaY});
      }
    }
  };

  onDragStart = (point: Point) => {
    if (this.state.alt) this.handlers.onDragStarted(point);
  };

  onDragEnd = () => {
    if (this.state.changing) this.handlers.onDragEnded();
  };

  render() {
    const {
      name,
      disabled,
      placeholder,
      style,
      min,
      max,
      // We don't use _onChange here because it is coming from `handlers.onChange`. This is to
      // fix flow, which was complaining because of the `...rest` spread saying that the onChange
      // prop is different from the one that Input expects. We need to spread here so that we can
      // pass in css props into the Sigma component
      onChange: _onChange,
      gridColumn,
      gridColumnStart,
      gridColumnEnd,
      gridRow,
      gridRowStart,
      gridRowEnd,
      alignSelf,
      justifySelf,
      placeSelf,
      alwaysShowRightInputTicks,
      allowKeywordDrag = false,
      ...rest
    } = this.props;
    const {inputValue, focus, invalid, alt} = this.state;
    const {onFocus, onBlur, onChange, onKeyDown, onKeyUp} = this.handlers;

    const gridStyle: Partial<React.CSSProperties> = {};

    if (gridColumn != null) gridStyle.gridColumn = gridColumn;
    if (gridColumnStart != null) gridStyle.gridColumnStart = gridColumnStart;
    if (gridColumnEnd != null) gridStyle.gridColumnEnd = gridColumnEnd;
    if (gridRow != null) gridStyle.gridRow = gridRow;
    if (gridRowStart != null) gridStyle.gridRowStart = gridRowStart;
    if (gridRowEnd != null) gridStyle.gridRowEnd = gridRowEnd;
    if (alignSelf != null) gridStyle.alignSelf = alignSelf;
    if (justifySelf != null) gridStyle.justifySelf = justifySelf;
    if (placeSelf != null) gridStyle.placeSelf = placeSelf;

    const inputDisabled = min >= max || disabled;

    // Check if input value is non-numeric and should disable drag/arrows
    // Empty string should be treated as numeric (allow drag)
    const shouldDisableForKeyword =
      inputValue !== '' &&
      Number.isNaN(Number(inputValue)) &&
      !allowKeywordDrag;

    return (
      <>
        <DragGhost
          dragging={!inputDisabled && this.state.changing}
          cursor={shouldDisableForKeyword ? undefined : 'ew-resize'}
        />

        <PseudoProvider>
          {({hover}) => (
            <Grid
              style={{...gridStyle, ...style}}
              data-legacy-ds="WebflowNumberInput"
            >
              <StyledTextInput
                {...rest}
                style={
                  alt && !inputDisabled && !shouldDisableForKeyword
                    ? inputResizeCursor
                    : undefined
                }
                // Allows undoing/redoing (in ShortcutHelper.js) if the Input is focused
                data-wf-text-input
                name={name}
                value={inputValue}
                placeholder={placeholder}
                disabled={inputDisabled}
                invalid={!this.state.changing && invalid}
                focus={focus}
                onFocus={(ev: React.FocusEvent<HTMLInputElement>) => {
                  ev.target.select();
                  onFocus(ev);
                }}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                onChange={onChange}
                // @ts-expect-error - TS2322 - `onWheel` isn't on the legacy Input prop type, but is forwarded to the underlying <input> at runtime.
                onWheel={focus ? this.handleWheel : undefined}
                onDrop={disallowTextDragging}
                data-automation-id={this.props['data-automation-id']}
                autoComplete="off"
                mode="delta"
                dragging={this.state.changing}
                onDragStart={this.onDragStart}
                onDrag={this.handlers.onDragged}
                onDragEnd={this.onDragEnd}
              />
              {inputDisabled || shouldDisableForKeyword
                ? null
                : (alwaysShowRightInputTicks || (this.state.alt && hover)) && (
                    <div
                      style={{position: 'absolute', right: 0, height: '100%'}}
                    >
                      <WebflowRightInputTicks
                        onTopClick={this.handlers.onRightUpTickClick}
                        onBottomClick={this.handlers.onRightDownTickClick}
                      />
                    </div>
                  )}
            </Grid>
          )}
        </PseudoProvider>
      </>
    );
  }
}

const Grid = emotionStyled('div')({
  display: 'grid',
  position: 'relative',
  gridTemplateColumns: '1fr auto',
  transform: 'translate3d(0,0,0)',
});
